/**
 * The kitchen's side of a print macro render on the api server: start a job, follow it
 * until it has finished, fetch its pdfs. The api renders the pages in its own chrome
 * (fed-nutrition-api macroRender*.ts); every call here goes through the app's api client,
 * which carries the login - the api reads the token from the Authorization header only,
 * so a plain link to a pdf would arrive anonymous.
 */
import api from '@/api';

const RENDER_URL = 'v2/print-macros/render';
const POLL_MS = 2000;

/** a job the api is still working on: rendering its pages, or posting them to slack */
export function isRenderActive(job) {
  return !!job && ['queued', 'running', 'delivering'].includes(job.status);
}

/** POST a render request (macro-steps.js serverRenderRequest / pageRenderRequest); returns the job */
export async function startRender(request) {
  const {data} = await api.post(RENDER_URL, request);
  return data;
}

/**
 * Fetch the job every couple of seconds until it is no longer active, calling onUpdate
 * with each fresh copy. Returns a handle with stop() for when the page goes away; the
 * promise resolves with the final job, or rejects when the api stops answering.
 */
export function followRender(jobId, {onUpdate = () => {}, pollMs = POLL_MS} = {}) {
  let timer = null;
  let stopped = false;
  const finished = new Promise((resolve, reject) => {
    const poll = async () => {
      timer = null;
      let job;
      try {
        ({data: job} = await api.get(`${RENDER_URL}/${jobId}`));
      } catch (e) {
        reject(e);
        return;
      }
      if (stopped) {
        return;
      }
      onUpdate(job);
      if (isRenderActive(job)) {
        timer = setTimeout(poll, pollMs);
      } else {
        resolve(job);
      }
    };
    poll();
  });
  return {
    finished,
    stop() {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

/** a finished page's pdf as a Blob */
export async function fetchRenderedPdf(jobId, index) {
  const {data} = await api.get(`${RENDER_URL}/${jobId}/files/${index}`, {responseType: 'blob'});
  return data;
}

export function cancelRender(jobId) {
  return api.delete(`${RENDER_URL}/${jobId}`);
}

/** the api's slack setup: whether it can post pdfs, and its default channel {name, id} */
export async function fetchSlackConfig() {
  const {data} = await api.get('v2/print-macros/slack');
  return {configured: !!data.configured, channel: data.channel || null};
}

/** post a finished download render's pdfs to a channel; returns the job with its slack outcome */
export async function postRenderToSlack(jobId, channel) {
  const {data} = await api.post(`${RENDER_URL}/${jobId}/slack`, {channel});
  return data;
}
